import { useState, useCallback } from 'react';
import { message } from 'antd';
import { j208Api, FJ190, FJ191, FJ192 } from '../../../services/j208Api';
import { Mode } from './useJ208Mode';

export function useJ208Record() {
  const [record, setRecord] = useState<FJ190 | null>(null);
  const [formData, setFormData] = useState<Partial<FJ190>>({});
  const [details, setDetails] = useState<Partial<FJ191>[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await j208Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback((key: keyof FJ190, value: FJ190[keyof FJ190]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const getField = useCallback((key: keyof FJ190): FJ190[keyof FJ190] => {
    return (formData as Record<string, FJ190[keyof FJ190]>)[key] ?? '';
  }, [formData]);

  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FJ190) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FJ190,
        details: details as FJ191[],
      };
      if (mode === 'add') {
        const result = await j208Api.create(req);
        message.success(`新增成功: ${result.fj190_01}`);
        onSuccess(result);
      } else {
        const result = await j208Api.update(formData.fj190_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '儲存失敗';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record?.fj190_01) return;
    setLoading(true);
    try {
      await j208Api.delete(record.fj190_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ fj190_80: 'N' });
    setDetails([]);
  }, []);

  const applyResult = useCallback((result: FJ190) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        fj191_02: prev.length + 1,
        fj191_10: '', fj191_11: '', fj191_12: '',
        fj191_121: '', fj191_122: '', fj191_12a: '',
        fj191_13: 0, fj191_14: '',
        fj191_15: 0, fj191_151: 0,
        fj191_16: 0, fj191_161: 0,
        fj191_17: '', fj191_171: '',
        fj191_18: 0,
        fj191_19: '', fj191_20: '', fj191_21: '',
        fj191_22: 0, fj191_23: 0, fj191_24: 0, fj191_25: 0,
        fj191_26: '',
        board_numbers: [],
      },
    ]);
  }, []);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const updateBoardNumbers = useCallback((detailIdx: number, boardNumbers: Partial<FJ192>[]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[detailIdx]) {
        next[detailIdx] = {
          ...next[detailIdx],
          board_numbers: boardNumbers as FJ192[],
        };
      }
      return next;
    });
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow, updateBoardNumbers,
  };
}
